import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Receipt, 
  Check, 
  X, 
  Clock, 
  Warning,
  Pencil,
  Bank,
  Calendar,
  CurrencyCircleDollar
} from '@phosphor-icons/react';
import { Trash2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function CheckManagement() {
  const { user } = useAuth();
  const [checks, setChecks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [checkTypeFilter, setCheckTypeFilter] = useState('');  // outgoing or incoming
  
  const [formData, setFormData] = useState({
    check_no: '',
    bank_name: '',
    amount: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    recipient: '',
    description: '',
    check_type: 'outgoing',  // outgoing: Verilen, incoming: Alınan
  });

  const STATUS_OPTIONS = [
    { value: 'pending', label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
    { value: 'cashed', label: 'Tahsil Edildi', color: 'bg-green-100 text-green-700 border-green-300' },
    { value: 'bounced', label: 'Karşılıksız', color: 'bg-red-100 text-red-700 border-red-300' },
    { value: 'cancelled', label: 'İptal', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  ];

  const CHECK_TYPE_OPTIONS = [
    { value: 'outgoing', label: 'Verilen Çek', color: 'bg-red-100 text-red-700' },
    { value: 'incoming', label: 'Alınan Çek', color: 'bg-green-100 text-green-700' },
  ];

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, [statusFilter, checkTypeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (checkTypeFilter) params.push(`check_type=${checkTypeFilter}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      
      const [checksRes, summaryRes] = await Promise.all([
        api.get(`/checks${queryString}`),
        api.get('/checks/summary')
      ]);
      setChecks(checksRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingCheck(null);
    setFormData({
      check_no: '',
      bank_name: '',
      amount: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      recipient: '',
      description: '',
      check_type: 'outgoing',
    });
  };

  const handleSubmit = async () => {
    if (!formData.check_no || !formData.bank_name || !formData.amount || !formData.due_date || !formData.recipient) {
      toast.error('Zorunlu alanları doldurun');
      return;
    }

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (editingCheck) {
        await api.put(`/checks/${editingCheck.id}`, payload);
        toast.success('Çek güncellendi');
      } else {
        await api.post('/checks', payload);
        toast.success('Çek eklendi');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleEdit = (check) => {
    setEditingCheck(check);
    setFormData({
      check_no: check.check_no,
      bank_name: check.bank_name,
      amount: check.amount.toString(),
      issue_date: check.issue_date,
      due_date: check.due_date,
      recipient: check.recipient,
      description: check.description || '',
      check_type: check.check_type || 'outgoing',
    });
    setIsDialogOpen(true);
  };

  const handleStatusChange = async (checkId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'cashed') {
        updateData.cashed_date = new Date().toISOString().split('T')[0];
      }
      await api.put(`/checks/${checkId}`, updateData);
      toast.success('Çek durumu güncellendi');
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDelete = async (checkId) => {
    if (!confirm('Bu çeki silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/checks/${checkId}`);
      toast.success('Çek silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={opt.color}>{opt.label}</Badge>;
  };

  const isOverdue = (dueDate, status) => {
    if (status !== 'pending') return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="check-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Çek Takibi</h1>
            <p className="text-muted-foreground">Alınan ve verilen çeklerin takibi ve yönetimi</p>
          </div>
          {isManager && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-check-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Çek Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingCheck ? 'Çek Düzenle' : 'Yeni Çek Ekle'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Çek Türü Seçimi */}
                  <div>
                    <Label>Çek Türü *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Button
                        type="button"
                        variant={formData.check_type === 'outgoing' ? 'default' : 'outline'}
                        className={formData.check_type === 'outgoing' ? 'bg-red-600 hover:bg-red-700' : ''}
                        onClick={() => setFormData({ ...formData, check_type: 'outgoing' })}
                      >
                        Verilen Çek
                      </Button>
                      <Button
                        type="button"
                        variant={formData.check_type === 'incoming' ? 'default' : 'outline'}
                        className={formData.check_type === 'incoming' ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => setFormData({ ...formData, check_type: 'incoming' })}
                      >
                        Alınan Çek
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Çek No *</Label>
                      <Input
                        value={formData.check_no}
                        onChange={(e) => setFormData({ ...formData, check_no: e.target.value })}
                        placeholder="123456"
                        data-testid="check-no-input"
                      />
                    </div>
                    <div>
                      <Label>Banka *</Label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        placeholder="Ziraat Bankası"
                        data-testid="check-bank-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Tutar (₺) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="10000"
                      data-testid="check-amount-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Keşide Tarihi *</Label>
                      <Input
                        type="date"
                        value={formData.issue_date}
                        onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                        data-testid="check-issue-date"
                      />
                    </div>
                    <div>
                      <Label>Vade Tarihi *</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        data-testid="check-due-date"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>{formData.check_type === 'outgoing' ? 'Kime Verildi' : 'Kimden Alındı'} *</Label>
                    <Input
                      value={formData.recipient}
                      onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
                      placeholder={formData.check_type === 'outgoing' ? 'Tedarikçi adı' : 'Müşteri adı'}
                      data-testid="check-recipient-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Açıklama</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Çek ile ilgili notlar..."
                      rows={2}
                    />
                  </div>
                  
                  <Button onClick={handleSubmit} className="w-full" data-testid="save-check-btn">
                    {editingCheck ? 'Güncelle' : 'Kaydet'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Summary Cards */}
        {summary && (
          <>
            {/* Verilen vs Alınan Özeti */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-2 border-red-300 bg-red-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-700 font-medium uppercase">Verilen Çekler</p>
                      <p className="text-2xl font-bold text-red-700">{formatCurrency(summary.outgoing_amount || 0)}</p>
                      <p className="text-xs text-red-600">{summary.outgoing_count || 0} adet • Bekleyen: {formatCurrency(summary.outgoing_pending || 0)}</p>
                    </div>
                    <div className="p-3 bg-red-200 rounded-full">
                      <Receipt className="w-6 h-6 text-red-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-300 bg-green-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 font-medium uppercase">Alınan Çekler</p>
                      <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.incoming_amount || 0)}</p>
                      <p className="text-xs text-green-600">{summary.incoming_count || 0} adet • Bekleyen: {formatCurrency(summary.incoming_pending || 0)}</p>
                    </div>
                    <div className="p-3 bg-green-200 rounded-full">
                      <CurrencyCircleDollar className="w-6 h-6 text-green-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detay Kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="border-2 border-slate-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-slate-100 rounded-lg shrink-0">
                      <Receipt className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground uppercase truncate">Toplam Çek</p>
                      <p className="text-lg font-bold">{formatCurrency(summary.total_amount)}</p>
                      <p className="text-xs text-muted-foreground">{summary.total_count} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-yellow-200 bg-yellow-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-yellow-200 rounded-lg shrink-0">
                      <Clock className="w-4 h-4 text-yellow-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-yellow-700 uppercase truncate">Bekleyen</p>
                      <p className="text-lg font-bold text-yellow-700">{formatCurrency(summary.pending_amount)}</p>
                      <p className="text-xs text-yellow-600">{summary.pending_count} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-green-200 rounded-lg shrink-0">
                      <Check className="w-4 h-4 text-green-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-green-700 uppercase truncate">Tahsil Edilen</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(summary.cashed_amount)}</p>
                      <p className="text-xs text-green-600">{summary.cashed_count} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-200 bg-red-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-red-200 rounded-lg shrink-0">
                      <X className="w-4 h-4 text-red-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-red-700 uppercase truncate">Karşılıksız</p>
                      <p className="text-lg font-bold text-red-700">{formatCurrency(summary.bounced_amount)}</p>
                      <p className="text-xs text-red-600">{summary.bounced_count} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-200 rounded-lg shrink-0">
                      <Calendar className="w-4 h-4 text-blue-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-blue-700 uppercase truncate">Bu Ay Vadeli</p>
                      <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.due_this_month_amount)}</p>
                      <p className="text-xs text-blue-600">{summary.due_this_month_count} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-200 rounded-lg shrink-0">
                      <Warning className="w-4 h-4 text-orange-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-orange-700 uppercase truncate">Vadesi Geçen</p>
                      <p className="text-lg font-bold text-orange-700">{formatCurrency(summary.overdue_amount)}</p>
                      <p className="text-xs text-orange-600">{summary.overdue_count} adet</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label>Çek Türü:</Label>
                <Select value={checkTypeFilter || "all"} onValueChange={(v) => setCheckTypeFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="check-type-filter">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="outgoing">Verilen Çekler</SelectItem>
                    <SelectItem value="incoming">Alınan Çekler</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label>Durum:</Label>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Check List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Çek Listesi ({checks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : checks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt size={48} className="mx-auto mb-3 opacity-50" />
                <p>Henüz çek kaydı yok</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tür</TableHead>
                      <TableHead>Çek No</TableHead>
                      <TableHead>Banka</TableHead>
                      <TableHead>Kime/Kimden</TableHead>
                      <TableHead>Keşide</TableHead>
                      <TableHead>Vade</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-center">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {checks.map((check) => (
                      <TableRow 
                        key={check.id} 
                        className={`${isOverdue(check.due_date, check.status) ? 'bg-red-50' : ''} ${check.check_type === 'incoming' ? 'bg-green-50/50' : ''}`}
                        data-testid={`check-row-${check.id}`}
                      >
                        <TableCell>
                          <Badge className={check.check_type === 'incoming' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}>
                            {check.check_type === 'incoming' ? 'Alınan' : 'Verilen'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono font-medium">{check.check_no}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Bank className="w-4 h-4 text-muted-foreground" />
                            {check.bank_name}
                          </div>
                        </TableCell>
                        <TableCell>{check.recipient}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(check.issue_date)}</TableCell>
                        <TableCell>
                          <span className={isOverdue(check.due_date, check.status) ? 'text-red-600 font-semibold' : ''}>
                            {formatDate(check.due_date)}
                          </span>
                          {isOverdue(check.due_date, check.status) && (
                            <Badge variant="destructive" className="ml-2 text-xs">Gecikmiş</Badge>
                          )}
                        </TableCell>
                        <TableCell className={`text-right font-bold ${check.check_type === 'incoming' ? 'text-green-600' : ''}`}>
                          {check.check_type === 'incoming' ? '+' : ''}{formatCurrency(check.amount)}
                        </TableCell>
                        <TableCell>
                          {isManager ? (
                            <Select 
                              value={check.status} 
                              onValueChange={(val) => handleStatusChange(check.id, val)}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            getStatusBadge(check.status)
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-1">
                            {isManager && (
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(check)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDelete(check.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
